/**
 * Spec Phase 7 Slice 7E — numeral event bus.
 *
 * Lightweight pub/sub for cross-component numeral interactions.
 * Slice 7F consumers:
 *   - Reference Signs dock — auto-scrolls + highlights row on click
 *   - Frozen Drawings dock — pulses figure card on hover
 *   - Examiner View dock — auto-opens when an undefined numeral
 *     is clicked
 *
 * Why a bus instead of prop-drilling the store:
 *   - Numeral chips are deeply nested inside dangerouslySetInnerHTML
 *     (auto sections) AND inside the contentEditable paragraph body
 *     (drafted sections). Threading store callbacks through
 *     dangerouslySetInnerHTML is impossible; event delegation
 *     bubbles the interaction up without coupling DOM nodes to
 *     React state.
 *   - Each consumer subscribes once on mount; chips publish
 *     fire-and-forget. Zero rerender pressure on listeners
 *     unrelated to the active numeral.
 *
 * No stale-listener risk: subscribe() returns an unsubscribe handle
 * the caller invokes from the useEffect cleanup. The bus uses Set
 * so dedup is automatic if a component double-subscribes.
 */

export type NumeralEventKind =
  | 'numeral:hover'
  | 'numeral:hover-out'
  | 'numeral:click';

export interface NumeralEvent {
  kind: NumeralEventKind;
  numeral: string;
  /** True when the chip's data-defined attribute is "false" — i.e.
   * the post-processor flagged the numeral as not present in the
   * matter's reference-signs registry. Slice 7E warn-state. */
  undefined_: boolean;
  /** Source surface so listeners can disambiguate (e.g. paragraph
   * vs auto-table). Optional — defaults to 'unknown'. */
  source?: 'paragraph' | 'reference_signs_table' | 'claims_mirror' | 'unknown';
}

type Listener = (ev: NumeralEvent) => void;

const _listeners: Set<Listener> = new Set();

export function subscribe(listener: Listener): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

export function publish(ev: NumeralEvent): void {
  // Set iteration with target=es5 needs downlevelIteration; CRA's
  // tsconfig validator strips that flag, so use forEach to avoid
  // fighting the build pipeline. Behavioural identical.
  _listeners.forEach((l) => {
    try {
      l(ev);
    } catch (e) {
      // Listener faults must not break other listeners or the
      // chip's own UX. Swallow + log.
      // eslint-disable-next-line no-console
      console.warn('numeralEventBus listener error', e);
    }
  });
}

/** Test-only escape hatch — the unit tests need to clear the
 * listener set between cases so cross-test leaks don't flake. */
export function _resetForTests(): void {
  _listeners.clear();
}
